// Shared types for the ported moderation dashboard (Entries).
// Mirrors webapp/src/schemas/{post,postManagement}.ts, but as plain TS types —
// client-side payload validation is intentionally not re-implemented here; the
// shared backend validates every write and returns { success:false, error } on
// rejection (see plan: "Scope simplifications").

export type UserEntryRevisionStatusType = 'accept' | 'reject' | 'defer1' | 'accept-hidden'

export type TranslationData = {
  verdict_label?: string
  summary?: string
  explanation?: string
  recommendations?: string[]
}

export type ScamData = {
  isScam?: boolean
  scamClaims?: string[]
  scamValue?: number
  translation?: TranslationData
  hidden?: boolean
  hideClassification?: boolean
}

export type CatalogItem = {
  id: number
  name_code: string
  name: string
  name_long?: string | null
  description?: string | null
  logo_url?: string | null
  url?: string | null
  tenant_ids?: string[]
  /** platform.is_public: false = hidden from the consumer apps (the default for new platforms) */
  is_public?: boolean
  created_at?: string | null
  updated_at?: string | null
}

export type Catalog = {
  platforms: CatalogItem[]
  messageTypes: CatalogItem[]
  callTypes: CatalogItem[]
}

export type TenantItem = {
  id: string
  name_code: string
  name: string
  name_long?: string | null
  description?: string | null
  logo_url?: string | null
  is_active?: boolean
  created_at?: string
  updated_at?: string
}

export type TenantAccessItem = {
  id: string
  user_id: string
  email?: string | null
  email_verified?: boolean
  created_at: string
}

export type SourceItem = {
  id: string
  name_code: string
  name: string
  tenant_ids?: string[]
  created_at?: string
  updated_at?: string
}

export type EmailTypeItem = {
  id: number
  name_code: string
  name: string
  description?: string | null
  tenant_id?: string | null
  created_at?: string
  updated_at?: string
  email_log_count?: number
}

export type EmailLogItem = {
  id: string
  created_at: string
  from: string
  to: string[]
  bcc?: string[] | null
  subject: string
  campaign?: string | null
  entry_id?: string | null
  user_entry_id?: string | null
  email_type_id?: number | null
  in_reply_to_id?: string | null
  status: 'sent' | 'failed'
  provider_id?: string | null
  error?: string | null
}

export type AccountActivityEventType =
  | 'account_created'
  | 'account_linked'
  | 'password_setup_requested'
  | 'password_reset_requested'
  | 'password_reset_completed'
  | 'login'
  | 'tenant_created'

export type AccountActivityLogItem = {
  id: string
  created_at: string
  event_type: AccountActivityEventType
  actor: 'admin' | 'user' | 'system'
  actor_ext_user_id?: string | null
  target_ext_user_id: string
  target_email?: string | null
  tenant_id?: string | null
  source: 'webapp' | 'backoffice'
  metadata?: Record<string, unknown> | null
}

// Raw entries frequently carry a free-text platform/type (e.g. "edp") instead
// of a resolved *_id when the submitter's value didn't get matched at ingest
// time — but the same name often does exist in the catalog. Falling back to a
// case-insensitive name match lets those entries still show a logo/icon.
export function findCatalogMatchByName(list: CatalogItem[], text?: string | null): CatalogItem | undefined {
  if (!text) return undefined
  const norm = text.trim().toLowerCase()
  if (!norm) return undefined
  return list.find((item) => item.name?.toLowerCase() === norm || item.name_code?.toLowerCase() === norm)
}

export type PipelineStep = {
  user_entry_id?: string
  step: string
  created_at: string
  // The classify_success json_info IS the object Hono returned to the
  // submitting client (postInsertion.ts writes `data` to the step and returns
  // the same `data`); the multibanco-validate step is the validator's inline
  // /mbvalidate answer. The keys below are the ones the public apps read —
  // see EntryDetail/UserFacing/ports.ts for who reads what.
  json_info?: {
    is_scam?: boolean | null
    confidence?: number
    scam_claims?: string[]
    scam_value?: number
    translation?: TranslationData
    /** Plain-text verdict the validators / domain checker show (`explanation ?? message`). */
    explanation?: string
    message?: string
    status?: string
    /** Bank lookup as the É Legítimo / domain pages expect it (nested) … */
    bk_result?: { beneficiario?: string; paymentowner?: string; lookup_status?: string; [key: string]: unknown }
    payments_metadata?: { entity_number?: string; reference?: string; value?: string; beneficiario?: string; [key: string]: unknown }
    /** … and as /mbvalidate actually stores it (top level). */
    beneficiario?: string
    paymentowner?: string
    entity_number?: string
    lookup_status?: string
    /** Set when the client was answered before classification finished. */
    classificationPending?: boolean
    error?: unknown
    hidden?: boolean
    hide_classification?: boolean
    pipeline_trace?: { description: string }[]
    [key: string]: unknown
  }
}

// raw_entries.original_payload — the submitted body minus the image, the
// Turnstile token and the fingerprint (backend/queries/posts/post.ts
// insertRawAndUserEntry). Exposed as raw_entry_original_payload on the flat
// views and as raw_entries.original_payload on /userentrydetail; parse it with
// EntryDetail/UserFacing/model.ts readOriginalPayload. These are the keys that
// say which public surface sent the entry and whether it waited for the verdict.
export type EntryOriginalPayloadHints = {
  source?: string
  postType?: string
  /** "extension" marks the microsites / extension form (É Legítimo, domain checker, …). */
  turnstile_key?: string
  simplePost?: boolean | null
  deferClassification?: boolean | null
  platform?: string
  ab_tests?: Record<string, unknown>
  [key: string]: unknown
}

// A user_entries row joined with raw_entries / processed_entries / pipeline steps,
// as returned by /allentries, /rawentries and /campaigns.
export type EntryRow = {
  id: string
  // Only on the /entry-detail shape (user_entries.* + users(email)), merged
  // into the list row by CaseEntryDetailPage's refresh: who submitted it.
  user_id?: string | null
  users?: { email?: string | null } | { email?: string | null }[] | null
  raw_entry_id?: string
  revision_status?: string
  revision_date?: string
  posted?: boolean
  created_at?: string
  entry_id?: string
  raw_entries?: any
  processed_entries?: any
  user_entry_pipeline_steps?: PipelineStep[]
  // processed_entries_flat is `any` (not narrowed here) since toSibsEntryAdapter/
  // toProcessedEntryAdapter read a couple dozen loosely-typed columns off it.
  // Which columns exist depends on the row shape stashed there:
  //
  // Cases (raw_entries_flat / processed_entries_flat) — bare identifier
  //   columns: user_entry_domain_id, domain_id, entity_reference_id,
  //   entity_reference_beneficiary, plus pipeline_steps/last_pipeline_step.
  //
  // Unified + campaign members (unified_entries_flat) — those bare columns
  //   were removed when campaigns became derived. Instead:
  //   last_user_entry_domain_id / last_domain_id / last_domain_url /
  //     last_domain_manual_inactive, and the entity_reference equivalents —
  //     this entry's MOST RECENT link only. The `last_` prefix is
  //     load-bearing: these are partial whenever entry_domain_count > 1.
  //     Use them for display and for joining outward (last_user_entry_domain_id
  //     is the FK entry_domain_data/entry_browser_data/domain_probe_run hang
  //     off); never filter on one to answer "which entries touch domain X".
  //   entry_domains / entry_entity_references — this entry's COMPLETE set of
  //     own links, jsonb arrays; each element carries its own link id
  //     (user_entry_domain_id / user_entry_entityreference_id) plus
  //     original_url. entry_domain_count / entry_entity_reference_count.
  //   campaign_id (text, 'd:<uuid>' or 'e:<uuid>' — NOT a uuid, and NOT a
  //     stable key: see campaignTypes.ts) / campaign_type /
  //     campaign_domains / campaign_entity_references / campaign_domain_count /
  //     campaign_entity_reference_count / campaign_entry_count. The campaign
  //     arrays are the WHOLE campaign's identifiers — a superset of this
  //     entry's own, since the campaign can include an identifier only a
  //     co-member entry carries. That difference is what explains why two
  //     entries are grouped, so don't conflate the two sets.
  //   No pipeline_steps/last_pipeline_step, and no campaign_name/
  //     campaign_revision_* — all dropped from this view.
  processed_entries_flat?: any
  [key: string]: unknown
}
